import React, { useState, Fragment } from 'react';
import { Box, Flex, Text, Avatar, Button, Input, FormControl, FormLabel, useToast, Divider, Stack } from '@chakra-ui/react';
import { LogOut, MapPin, Phone, Mail, User as UserIcon, MessageCircle, Instagram, Youtube } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
export const Profile: React.FC = () => {
  const {
    user,
    updateUser,
    login,
    logout
  } = useAppContext();
  const toast = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    mobile: user?.mobile || '',
    email: user?.email || '',
    address: user?.address || '',
    city: 'Ghazipur',
    pincode: user?.pincode || ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSave = () => {
    if (!formData.name || !formData.mobile) {
      toast({
        title: 'Name and Mobile required',
        status: 'error'
      });
      return;
    }
    updateUser(formData);
    setIsEditing(false);
    toast({
      title: 'Profile updated',
      status: 'success'
    });
  };
  const handleLogin = () => {
    if (!formData.name || !formData.mobile) {
      toast({
        title: 'Enter Name and Mobile',
        status: 'error'
      });
      return;
    }
    login(formData);
    toast({
      title: 'Welcome to Sky Brand!',
      status: 'success'
    });
  };
  if (!user) {
    return <Box p={6} bg="white" minH="100vh" className="animate-fade-in" data-id="element-645">
        <Flex direction="column" align="center" mb={8} mt={10} data-id="element-646">
          <Box bg="brand.50" p={5} borderRadius="full" mb={4} className="animate-float" data-id="element-647">
            <UserIcon size={36} color="var(--chakra-colors-brand-500)" data-id="element-648" />
          </Box>
          <Text fontSize="xl" fontWeight="800" color="gray.800" data-id="element-649">
            Welcome to Sky Brand
          </Text>
          <Text color="gray.400" textAlign="center" mt={2} fontSize="sm" data-id="element-650">
            Enter your details to get started
          </Text>
        </Flex>
        <FormControl mb={4} isRequired data-id="element-651">
          <FormLabel fontSize="sm" fontWeight="600" data-id="element-652">
            Full Name
          </FormLabel>
          <Input name="name" value={formData.name} onChange={handleChange} placeholder="Your name" borderRadius="12px" bg="gray.50" border="none" data-id="element-653" />
        </FormControl>
        <FormControl mb={6} isRequired data-id="element-654">
          <FormLabel fontSize="sm" fontWeight="600" data-id="element-655">
            Mobile Number
          </FormLabel>
          <Input name="mobile" type="tel" value={formData.mobile} onChange={handleChange} placeholder="10-digit number" borderRadius="12px" bg="gray.50" border="none" data-id="element-656" />
        </FormControl>
        <Button bg="brand.500" color="white" w="full" size="lg" borderRadius="12px" _hover={{
        bg: 'brand.600'
      }} onClick={handleLogin} fontWeight="700" data-id="element-657">
          Continue
        </Button>
      </Box>;
  }
  const fields = ['name', 'mobile', 'email', 'address', 'pincode'];
  const filledFields = fields.filter(f => user[f as keyof typeof user]);
  const completionPercent = Math.round(filledFields.length / fields.length * 100);
  return <Box bg="gray.50" minH="100%" pb="80px" className="animate-fade-in" data-id="element-658">
      {/* Profile Header */}
      <Box className="gradient-premium" color="white" p={6} pt={8} data-id="element-659">
        <Flex align="center" gap={4} data-id="element-660">
          <Avatar size="lg" name={user.name} bg="white" color="brand.500" fontWeight="800" data-id="element-661" />
          <Box data-id="element-662">
            <Text fontSize="lg" fontWeight="700" data-id="element-663">
              {user.name}
            </Text>
            <Text fontSize="sm" opacity={0.8} data-id="element-664">
              {user.mobile}
            </Text>
          </Box>
        </Flex>
        <Box mt={5} bg="whiteAlpha.200" p={3} borderRadius="12px" data-id="element-665">
          <Flex justify="space-between" mb={1} data-id="element-666">
            <Text fontSize="xs" fontWeight="500" data-id="element-667">
              Profile Completion
            </Text>
            <Text fontSize="xs" fontWeight="700" data-id="element-668">
              {completionPercent}%
            </Text>
          </Flex>
          <Box w="full" h="4px" bg="whiteAlpha.300" borderRadius="full" data-id="element-669">
            <Box w={`${completionPercent}%`} h="full" bg="white" borderRadius="full" transition="width 0.5s ease" data-id="element-670" />
          </Box>
        </Box>
      </Box>

      {/* Personal Details */}
      <Box p={4} mt={2} data-id="element-671">
        <Flex justify="space-between" align="center" mb={3} data-id="element-672">
          <Text fontSize="md" fontWeight="700" color="gray.800" data-id="element-673">
            Personal Details
          </Text>
          {!isEditing && <Button size="sm" variant="ghost" color="brand.500" fontWeight="600" onClick={() => setIsEditing(true)} data-id="element-674">
              Edit
            </Button>}
        </Flex>
        <Box bg="white" p={4} borderRadius="16px" shadow="sm" data-id="element-675">
          {[{
          label: 'Full Name',
          name: 'name',
          icon: <UserIcon size={15} color="gray" data-id="element-676" />,
          value: user.name
        }, {
          label: 'Mobile',
          name: 'mobile',
          icon: <Phone size={15} color="gray" data-id="element-677" />,
          value: user.mobile
        }, {
          label: 'Email',
          name: 'email',
          icon: <Mail size={15} color="gray" data-id="element-678" />,
          value: user.email || 'Not provided',
          type: 'email'
        }].map((field, idx) => <Fragment key={field.name} data-id="element-679">
              <FormControl mb={2} data-id="element-680">
                <FormLabel fontSize="10px" color="gray.400" mb={0.5} fontWeight="600" data-id="element-681">
                  {field.label}
                </FormLabel>
                {isEditing ? <Input name={field.name} value={formData[field.name as keyof typeof formData]} onChange={handleChange} size="sm" borderRadius="8px" data-id="element-682" /> : <Flex align="center" gap={2} data-id="element-683">
                    {field.icon}
                    <Text fontSize="sm" data-id="element-684">{field.value}</Text>
                  </Flex>}
              </FormControl>
              {idx < 2 && <Divider my={2} data-id="element-685" />}
            </Fragment>)}
          <Divider my={2} data-id="element-686" />
          <FormControl mb={2} data-id="element-687">
            <FormLabel fontSize="10px" color="gray.400" mb={0.5} fontWeight="600" data-id="element-688">
              Address
            </FormLabel>
            {isEditing ? <Box data-id="element-689">
                <Input name="address" value={formData.address} onChange={handleChange} size="sm" mb={2} borderRadius="8px" placeholder="Street" data-id="element-690" />
                <Flex gap={2} data-id="element-691">
                  <Input name="city" value={formData.city} isReadOnly bg="gray.100" size="sm" borderRadius="8px" data-id="element-692" />
                  <Input name="pincode" value={formData.pincode} onChange={handleChange} size="sm" borderRadius="8px" placeholder="Pincode" data-id="element-693" />
                </Flex>
              </Box> : <Flex align="start" gap={2} data-id="element-694">
                <MapPin size={15} color="gray" data-id="element-695" />
                <Text fontSize="sm" data-id="element-696">
                  {user.address ? `${user.address}, ${user.city} - ${user.pincode}` : 'Not provided'}
                </Text>
              </Flex>}
          </FormControl>
          {isEditing && <Flex gap={2} mt={4} data-id="element-697">
              <Button flex={1} size="sm" variant="ghost" onClick={() => setIsEditing(false)} borderRadius="10px" data-id="element-698">
                Cancel
              </Button>
              <Button flex={1} size="sm" bg="brand.500" color="white" onClick={handleSave} borderRadius="10px" _hover={{
            bg: 'brand.600'
          }} data-id="element-699">
                Save
              </Button>
            </Flex>}
        </Box>
      </Box>

      {/* Customer Care */}
      <Box p={4} data-id="element-700">
        <Box bg="white" p={4} borderRadius="16px" shadow="sm" data-id="element-701">
          <Text fontSize="md" fontWeight="700" mb={3} data-id="element-702">
            📞 Customer Care
          </Text>
          <Button as="a" href="https://chat.whatsapp.com/IkRgOt1lq4OGk2v9SDVqYa?mode=gi_t" target="_blank" rel="noopener noreferrer" bg="green.500" color="white" leftIcon={<MessageCircle size={18} data-id="element-704" />} w="full" justifyContent="flex-start" borderRadius="12px" _hover={{
          bg: 'green.600',
          transform: 'translateY(-1px)'
        }} transition="all 0.3s ease" shadow="sm" data-id="element-703">
            Join WhatsApp Group
          </Button>
        </Box>
      </Box>

      {/* Follow Us */}
      <Box px={4} data-id="element-705">
        <Box bg="white" p={4} borderRadius="16px" shadow="sm" data-id="element-706">
          <Text fontSize="md" fontWeight="700" mb={3} data-id="element-707">
            📱 Follow Us
          </Text>
          <Stack spacing={2} data-id="element-708">
            <Button as="a" href="https://www.instagram.com/skybrand.yourbelive?igsh=MXQzeTRzeW5icHQyYg==" target="_blank" rel="noopener noreferrer" bgGradient="linear(to-r, #833AB4, #FD1D1D, #F77737)" color="white" leftIcon={<Instagram size={18} data-id="element-710" />} w="full" justifyContent="flex-start" borderRadius="12px" _hover={{
            opacity: 0.9
          }} shadow="sm" data-id="element-709">
              Follow on Instagram
            </Button>
            <Button as="a" href="https://youtube.com/@skybrand_12?si=gbLTwnUZ-TgmycEB" target="_blank" rel="noopener noreferrer" bg="red.600" color="white" leftIcon={<Youtube size={18} data-id="element-712" />} w="full" justifyContent="flex-start" borderRadius="12px" _hover={{
            bg: 'red.700'
          }} shadow="sm" data-id="element-711">
              Subscribe on YouTube
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Logout */}
      <Box p={4} data-id="element-713">
        <Button w="full" variant="outline" borderColor="red.300" color="red.500" leftIcon={<LogOut size={16} data-id="element-715" />} borderRadius="12px" _hover={{
        bg: 'red.50'
      }} onClick={() => {
        logout();
        toast({
          title: 'Logged out',
          status: 'info'
        });
      }} data-id="element-714">
          Logout
        </Button>
      </Box>
    </Box>;
};